import React, { useContext, useEffect, useState } from "react";
import { Tab } from "@headlessui/react";
import { TextInput, Label, Select } from "flowbite-react";
import DataTable from "react-data-table-component";
import GlobalContext from "../connections/GlobalContext";
import engineGif from "../image/engine.gif";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import axios from "axios";
// Chart.js imports for graph
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
} from "chart.js";
ChartJS.register(LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend);

const Report = ({ theme }) => {
  const { globalValue } = useContext(GlobalContext);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    purchase: [],
    sales: [],
    service_log: [],
    income: [],
    expenses: [],
    pending_customers: [],
  });
  const [activeTab, setActiveTab] = useState("sales");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [incomeCategories, setIncomeCategories] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  // For P&L tab
  const [profitLossData, setProfitLossData] = useState([]);
  const [monthlyGraphData, setMonthlyGraphData] = useState({ labels: [], income: [], expense: [] });

  useEffect(() => {
    fetchData();
    fetchIncomeCategories();
    fetchExpenseCategories();
  }, []);

  const fetchData = () => {
    setLoading(true);
    const branch = sessionStorage.getItem("branch");
    fetch(`${globalValue}/report?branch=${encodeURIComponent(branch)}`, {
      method: "GET",
      headers: {
        "x-access-token": sessionStorage.getItem("token"),
      },
    })
      .then((res) => res.json())
      .then((dat) => {
        const initialData = {
          purchase: [],
          sales: [],
          service_log: [],
          income: [],
          expenses: [],
          pending_customers: [],
        };
        setData({ ...initialData, ...dat });
      })
      .catch((error) => console.error("Error fetching data:", error))
      .finally(() => setLoading(false));
  };

  const fetchIncomeCategories = () => {
    setLoading(true);
    axios
      .get(`${globalValue}/category_income_display`)
      .then((res) => setIncomeCategories(res.data))
      .catch((err) => console.error("Error fetching income categories:", err))
      .finally(() => setLoading(false));
  };

  const fetchExpenseCategories = () => {
    setLoading(true);
    axios
      .get(`${globalValue}/category_display`)
      .then((res) => setExpenseCategories(res.data))
      .catch((err) => console.error("Error fetching expense categories:", err))
      .finally(() => setLoading(false));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    return `${String(date.getDate()).padStart(2, "0")}/${String(
      date.getMonth() + 1
    ).padStart(2, "0")}/${date.getFullYear()}`;
  };

  const filterByDate = (items, dateKey) => {
    if (!Array.isArray(items)) return [];
    return items.filter((item) => {
      const itemDate = new Date(item[dateKey]);
      const from = fromDate ? new Date(fromDate) : null;
      const to = toDate ? new Date(toDate) : null;
      if (to) to.setHours(23, 59, 59, 999);
      return (!from || itemDate >= from) && (!to || itemDate <= to);
    });
  };

  const getFilteredData = (currentTab) => {
    let itemsToFilter = [];
    let dateKey = "date";

    if (currentTab === "sales") {
      itemsToFilter = data.sales;
    } else if (currentTab === "purchase") {
      itemsToFilter = data.purchase;
    } else if (currentTab === "income") {
      itemsToFilter = data.income;
    } else if (currentTab === "expenses") {
      itemsToFilter = data.expenses;
    } else if (currentTab === "service_log") {
      itemsToFilter = data.service_log;
      dateKey = "s_date";
    } else if (currentTab === "pending_customers") {
      itemsToFilter = data.pending_customers;
    }

    let filteredItems =
      currentTab === "pending_customers"
        ? itemsToFilter
        : filterByDate(itemsToFilter, dateKey);

    if (currentTab === "income" || currentTab === "expenses") {
      if (selectedCategory === "others") {
        filteredItems = filteredItems.filter(
          (item) =>
            !item.category ||
            item.category.trim() === "" ||
            item.mode?.toLowerCase() !== "regular"
        );
      } else if (selectedCategory) {
        filteredItems = filteredItems.filter(
          (item) =>
            item.category?.toLowerCase() === selectedCategory.toLowerCase()
        );
      }

      if (search.trim()) {
        const keyword = search.toLowerCase();
        filteredItems = filteredItems.filter(
          (row) =>
            row.category?.toLowerCase().includes(keyword) ||
            String(row.amount).includes(keyword) ||
            row.mode?.toLowerCase().includes(keyword) ||
            row.customer_name?.toLowerCase().includes(keyword) ||
            row.vehicle_no?.toLowerCase().includes(keyword)
        );
      }
    } else if (currentTab === "pending_customers") {
      if (search.trim()) {
        const keyword = search.toLowerCase();
        filteredItems = filteredItems.filter(
          (row) =>
            (row.customer_name && row.customer_name.toLowerCase().includes(keyword)) ||
            (row.mobile_no && row.mobile_no.toLowerCase().includes(keyword)) ||
            (row.vehicle_no && row.vehicle_no.toLowerCase().includes(keyword)) ||
            (row.brand && row.brand.toLowerCase().includes(keyword)) ||
            (row.model && row.model.toLowerCase().includes(keyword)) ||
            (row.address && row.address.toLowerCase().includes(keyword))
        );
      }
    } else {
      if (search.trim()) {
        const keyword = search.toLowerCase();
        if (currentTab === "purchase") {
          filteredItems = filteredItems.filter(
            (row) =>
              row.bill_no?.toLowerCase().includes(keyword) ||
              row.vendor_name?.toLowerCase().includes(keyword)
          );
        } else if (currentTab === "sales") {
          filteredItems = filteredItems.filter(
            (row) =>
              row.name?.toLowerCase().includes(keyword) ||
              row.mobile_no?.toLowerCase().includes(keyword)
          );
        } else if (currentTab === "service_log") {
          filteredItems = filteredItems.filter(
            (row) =>
              row.customer_name?.toLowerCase().includes(keyword) ||
              row.mobile_no?.toLowerCase().includes(keyword)
          );
        }
      }
    }
    return filteredItems;
  };

  const formatNumber = (num) =>
    Number(num).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const getTotalAmount = (currentTab, filteredData) => {
    if (!Array.isArray(filteredData)) return 0;
    switch (currentTab) {
      case "sales":
        return filteredData.reduce(
          (sum, item) => sum + (Number(item.grand_total) || 0),
          0
        );
      case "purchase":
        return filteredData.reduce(
          (sum, item) => sum + (Number(item.total_amount) || 0),
          0
        );
      case "income":
      case "expenses":
        return filteredData.reduce(
          (sum, item) => sum + (Number(item.amount) || 0),
          0
        );
      case "service_log":
      case "pending_customers":
      default:
        return 0;
    }
  };

  const handleExportPDF = () => {
    const doc = new jsPDF();
    const title =
      "JALS AUTOMOBILES - " + activeTab.charAt(0).toUpperCase() + activeTab.slice(1) + " Report";

    doc.text(title, 14, 10);

    let startY = 15;
    if (fromDate || toDate) {
      doc.setFontSize(10);
      const fromText = fromDate ? `From: ${formatDate(fromDate)}` : "";
      const toText = toDate ? `To: ${formatDate(toDate)}` : "";
      doc.text(`${fromText}   ${toText}`, 14, 16);
      startY = 25;
    }

    let rows = [];
    let columns = [];
    let tableData = getFilteredData(activeTab);

    if (activeTab === "sales") {
      columns = ["Date", "Name", "Mobile", "Address", "Total"];
      rows = tableData.map((row) => [
        formatDate(row.date),
        row.name,
        row.mobile_no,
        row.address,
        row.grand_total,
      ]);
    } else if (activeTab === "purchase") {
      columns = ["Date", "Bill No", "Vendor", "Total", "Paid", "Mode"];
      rows = tableData.map((row) => [
        formatDate(row.date),
        row.bill_no,
        row.vendor_name,
        row.total_amount,
        row.amount_paid,
        row.pay_mode,
      ]);
    } else if (activeTab === "income") {
      columns = ["Date", "Customer Name", "Vehicle No", "Category", "Amount", "Mode"];
      rows = tableData.map((row) => [
        formatDate(row.date),
        row.customer_name || "-",
        row.vehicle_no || "-",
        row.category,
        row.amount,
        row.mode,
      ]);
    } else if (activeTab === "expenses") {
      columns = ["Date", "Category", "Amount", "staff/expense"];
      rows = tableData.map((row) => [
        formatDate(row.date),
        row.category,
        row.amount,
        row.mode,
      ]);
    }
    else if (activeTab === "service_log") {
      columns = ["Start Date", "Estimate ID", "Customer", "End Date", "Mode"];
      rows = tableData.map((row) => [
        formatDate(row.s_date),
        row.estimate_id,
        row.customer_name,
        formatDate(row.e_date),
        row.mode,
      ]);
    } else if (activeTab === "pending_customers") {
      columns = [
        "Customer Name",
        "Mobile No",
        "Brand",
        "Model",
        "Vehicle No",
        "Address",
      ];
      rows = tableData.map((row) => [
        row.customer_name,
        row.mobile_no,
        row.brand,
        row.model,
        row.vehicle_no,
        row.address,
      ]);
    } else if (activeTab === "profit_loss") {
      columns = ["Month", "Total Income", "Total Expense", "Profit / Loss"];
      rows = profitLossData.map((row) => [
        row.month,
        formatNumber(row.income),
        formatNumber(row.expense),
        formatNumber(row.profit)
      ]);
    }

    if (
      rows.length > 0 &&
      activeTab !== "service_log" &&
      activeTab !== "pending_customers"
    ) {
      let sumColumnIndex = null;
      let findIndex = 1;
      if (activeTab === "purchase") {
        findIndex = 3;
      }
      for (let i = rows[0].length - findIndex; i >= 0; i--) {
        if (rows.some((r) => !isNaN(parseFloat(r[i])))) {
          sumColumnIndex = i;
          break;
        }
      }
      if (sumColumnIndex !== null) {
        const totalValue = rows.reduce((sum, r) => {
          const val = parseFloat(r[sumColumnIndex]);
          return sum + (isNaN(val) ? 0 : val);
        }, 0);
        const totalRow = Array(rows[0].length).fill("");
        totalRow[sumColumnIndex - 1] = "TOTAL";
        totalRow[sumColumnIndex] = totalValue.toFixed(2);

        rows.push(totalRow);
      }
    }

    autoTable(doc, {
      head: [columns],
      body: rows,
      startY: startY,
      styles: { fontSize: 8 },
      didParseCell: (data) => {
        if (
          data.row.index === rows.length - 1 &&
          activeTab !== "service_log" &&
          activeTab !== "pending_customers"
        ) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fillColor = [220, 220, 220];
        }
      },
    });

    doc.save(`${activeTab}_report.pdf`);
  };

  const customStyles = {
    table: {
      style: {
        backgroundColor: theme === "dark" ? "rgb(31, 41, 55)" : "white",
      },
    },
    headCells: {
      style: {
        backgroundColor:
          theme === "dark" ? "rgb(55, 65, 81)" : "rgb(226, 232, 240)",
        color: theme === "dark" ? "white" : "black",
        fontWeight: "bold",
      },
    },
    cells: {
      style: {
        color: theme === "dark" ? "white" : "black",
        backgroundColor: theme === "dark" ? "rgb(31, 41, 55)" : "white",
      },
    },
    pagination: {
      style: {
        color: theme === "dark" ? "white" : "black",
        backgroundColor: theme === "dark" ? "rgb(31, 41, 55)" : "white",
      },
    },
  };

  const salesColumns = [
    {
      name: "Date",
      selector: (row) => formatDate(row.date),
      sortable: true,
      width: "120px",
    },
    { name: "Name", selector: (row) => row.name, sortable: true },
    { name: "Mobile", selector: (row) => row.mobile_no },
    { name: "Address", selector: (row) => row.address },
    {
      name: "Total",
      sortable: true,
      selector: row => Number(row.grand_total),
      cell: row => Number(row.grand_total).toFixed(2)
    }

  ];

  const purchaseColumns = [
    {
      name: "Date",
      selector: (row) => formatDate(row.date),
      sortable: true,
      width: "120px",
    },
    { name: "Bill No", selector: (row) => row.bill_no, sortable: true },
    { name: "Vendor", selector: (row) => row.vendor_name, sortable: true },
    {
      name: "Total",
      sortable: true,
      selector: row => Number(row.total_amount || 0),
      cell: row => Number(row.total_amount || 0).toFixed(2)
    },
    // { name: "Paid", selector: (row) => row.amount_paid, sortable: true },
    { name: "Mode", selector: (row) => row.pay_mode },
  ];

  const incomeColumns = [
    {
      name: "Date",
      selector: (row) => formatDate(row.date),
      sortable: true,
      width: "120px",
    },
    { name: "Customer Name", selector: (row) => row.customer_name || "-", sortable: true },
    { name: "Vehicle No", selector: (row) => row.vehicle_no || "-", sortable: true },
    { name: "Category", selector: (row) => row.category, sortable: true },
    {
      name: "Amount",
      sortable: true,
      selector: row => Number(row.amount || 0),
      cell: row => Number(row.amount || 0).toFixed(2)
    },
    { name: "Mode", selector: (row) => row.mode, sortable: true },
  ];

  const expenseColumns = [
    {
      name: "Date",
      selector: (row) => formatDate(row.date),
      sortable: true,
      width: "120px",
    },
    { name: "Category", selector: (row) => row.category, sortable: true },
    {
      name: "Amount",
      sortable: true,
      selector: row => Number(row.amount || 0),
      cell: row => Number(row.amount || 0).toFixed(2)
    },
    { name: "Staff/Expense", selector: (row) => row.mode, sortable: true },
  ];

  const serviceColumns = [
    {
      name: "Start Date",
      selector: (row) => formatDate(row.s_date),
      sortable: true,
      width: "120px",
    },
    { name: "Estimate ID", selector: (row) => row.estimate_id },
    { name: "Customer", selector: (row) => row.customer_name, sortable: true },
    { name: "Mobile No", selector: (row) => row.mobile_no },
    {
      name: "End Date",
      selector: (row) => formatDate(row.e_date),
      sortable: true,
    },
    { name: "Mode", selector: (row) => row.mode },
  ];

  const pendingCustomerColumns = [
    { name: "Customer Name", selector: (row) => row.customer_name, sortable: true },
    { name: "Mobile No", selector: (row) => row.mobile_no },
    { name: "Brand", selector: (row) => row.brand },
    { name: "Model", selector: (row) => row.model },
    { name: "Vehicle No", selector: (row) => row.vehicle_no },
    { name: "Address", selector: (row) => row.address },
  ];

  const profitLossColumns = [
    {
      name: "Month",
      selector: row => row.month,
      sortable: true
    },
    {
      name: "Total Income",
      sortable: true,
      selector: row => Number(row.income),            
      cell: row => formatNumber(row.income)           
    },
    {
      name: "Total Expense",
      sortable: true,
      selector: row => Number(row.expense),
      cell: row => formatNumber(row.expense)
    },
    {
      name: "Profit / Loss",
      sortable: true,
      selector: row => Number(row.profit),
      cell: row => (
        <span
          className={
            row.profit >= 0
              ? "text-green-600 font-semibold"
              : "text-red-600 font-semibold"
          }
        >
          {formatNumber(row.profit)}
        </span>
      )
    }
  ];



  const tabs = [
    "sales",
    "purchase",
    "income",
    "expenses",
    "service_log",
    "pending_customers",
    "profit_loss"
  ];


  const getMonthlySummary = (items, dateKey) => {
    if (!Array.isArray(items)) return {};
    const monthly = {};
    items.forEach(item => {
      const date = new Date(item[dateKey]);
      if (!isNaN(date)) {
        const key = `${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
        if (!monthly[key]) monthly[key] = 0;
        monthly[key] += Number(item.amount ?? item.total_amount ?? item.grand_total ?? 0);
      }
    });
    return monthly;
  };

  useEffect(() => {

    const incomeFiltered = filterByDate(data.income, "date");
    const expenseFiltered = filterByDate(data.expenses, "date");

    const incomeMonthly = getMonthlySummary(incomeFiltered, "date");
    const expenseMonthly = getMonthlySummary(expenseFiltered, "date");
    const labels = Array.from(new Set([
      ...Object.keys(incomeMonthly),
      ...Object.keys(expenseMonthly)
    ])).sort((a, b) => {

      const [am, ay] = a.split('/');
      const [bm, by] = b.split('/');
      return new Date(`${ay}-${am}-01`) - new Date(`${by}-${bm}-01`);
    });

    const plTbl = labels.map(label => {
      const inc = incomeMonthly[label] || 0;
      const exp = expenseMonthly[label] || 0;
      return {
        month: label,
        income: inc,
        expense: exp,
        profit: inc - exp
      };
    });
    if (plTbl.length) {
      plTbl.push({
        month: "Total",
        income: plTbl.reduce((a, b) => a + b.income, 0),
        expense: plTbl.reduce((a, b) => a + b.expense, 0),
        profit: plTbl.reduce((a, b) => a + b.income - b.expense, 0)
      });
    }
    setProfitLossData(plTbl);
    setMonthlyGraphData({
      labels,
      income: labels.map(l => incomeMonthly[l] || 0),
      expense: labels.map(l => expenseMonthly[l] || 0)
    });

  }, [fromDate, toDate, data]);


  const lineData = {
    labels: monthlyGraphData.labels,
    datasets: [
      {
        label: "Income",
        data: monthlyGraphData.income,
        borderColor: "#7c3aed",
        backgroundColor: "rgba(124,58,237,0.08)",
        tension: 0.3,
        fill: false
      },
      {
        label: "Expenses",
        data: monthlyGraphData.expense,
        borderColor: "#f87171",
        backgroundColor: "rgba(248,113,113,0.08)",
        tension: 0.3,
        fill: false
      }
    ]
  };

  const lineOptions = {
    plugins: {
      legend: {
        labels: {
          color: theme === "dark" ? "white" : "#222"
        }
      }
    },
    scales: {
      x: {
        ticks: { color: theme === "dark" ? "white" : "#222" }
      },
      y: {
        ticks: { color: theme === "dark" ? "white" : "#222" }
      }
    }
  };




  return (
    <div>
      {loading && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-200 bg-opacity-50 z-50">
          <img src={engineGif} alt="Loading..." className="w-24 h-24" />
        </div>
      )}
      <div
        className={`md:ml-[250px] w-full md:w-[calc(100%-250px)] mt-16 h-[92vh] flex justify-center items-center box-border ${theme === "dark"
          ? "bg-gray-900 text-white"
          : "bg-gray-100 text-gray-800"
          }`}
      >
        <div
          className={`w-[95%] md:w-[98%] h-[95%] px-4 py-2 rounded-xl shadow-xl overflow-auto ${theme === "dark"
            ? "bg-gray-800 text-gray-200"
            : "bg-white text-gray-800"
            }`}
        >
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Report Summary</h2>
            <button
              onClick={handleExportPDF}
              className="px-4 py-2 text-white bg-green-600 hover:bg-green-700 rounded"
            >
              Export PDF
            </button>
          </div>
          <div className="flex flex-wrap gap-4 mb-4">
            <div className="flex flex-col">
              <Label
                htmlFor="fromDate"
                value="From Date"
                className="mb-1 text-sm"
              />
              <input
                id="fromDate"
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                disabled={activeTab === "pending_customers"}
                className={`border rounded px-2 py-1 ${theme === "dark"
                  ? "bg-gray-700 border-gray-600"
                  : "bg-white border-gray-300"
                  }`}
              />
            </div>
            <div className="flex flex-col">
              <Label
                htmlFor="toDate"
                value="To Date"
                className="mb-1 text-sm"
              />
              <input
                id="toDate"
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                disabled={activeTab === "pending_customers"}
                className={`border rounded px-2 py-1 ${theme === "dark"
                  ? "bg-gray-700 border-gray-600"
                  : "bg-white border-gray-300"
                  }`}
              />
            </div>
            <div className="flex flex-col flex-1 min-w-[200px]">
              {activeTab === "income" ? (
                <div className="flex gap-4 flex-wrap">
                  <div className="s1">
                    <Label
                      htmlFor="categoryFilter"
                      value="Filter by Category"
                      className="mb-1 text-sm"
                    />
                    <Select
                      id="categoryFilter"
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      className={`${theme === "dark"
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                        }`}
                    >
                      <option value="">All Categories</option>
                      {activeTab === "income" &&
                        incomeCategories.map((cat) => (
                          <option key={cat.id} value={cat.category_name}>
                            {cat.category_name}
                          </option>
                        ))}
                      <option value="others">Others</option>
                    </Select>
                  </div>
                  <div>
                    <Label
                      htmlFor="search"
                      value="Search"
                      className="mb-1 text-sm"
                    />
                    <TextInput
                      id="search"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search..."
                    />
                  </div>
                </div>
              ) : activeTab === "expenses" ? (
                <>
                  <Label
                    htmlFor="categoryFilter"
                    value="Filter by Category"
                    className="mb-1 text-sm"
                  />
                  <Select
                    id="categoryFilter"
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className={`${theme === "dark"
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                      }`}
                  >
                    <option value="">All Categories</option>
                    {expenseCategories.map((cat) => (
                      <option key={cat.id} value={cat.category_name}>
                        {cat.category_name}
                      </option>
                    ))}
                    <option value="others">Others</option>
                  </Select>
                </>
              ) : activeTab !== "profit_loss" ? (
                <>
                  <Label
                    htmlFor="search"
                    value="Search"
                    className="mb-1 text-sm"
                  />
                  <TextInput
                    id="search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search..."
                  />
                </>
              ) : null}
            </div>
          </div>
          <Tab.Group
            selectedIndex={tabs.indexOf(activeTab)}
            onChange={i => {
              setActiveTab(tabs[i]);
              setSearch("");
              setSelectedCategory("");
            }}
          >
            <Tab.List className="flex justify-between w-full mb-4 border-b bg-gray-100 rounded-t-lg overflow-hidden">
              {tabs.map((tab) => (
                <Tab
                  key={tab}
                  className={({ selected }) =>
                    `flex-1 text-center px-6 py-3 transition-all duration-200 text-sm md:text-base ${selected
                      ? "bg-blue-600 text-white font-semibold"
                      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`
                  }
                >
                  {tab
                    .replace("_", " ")
                    .split(" ")
                    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
                    .join(" ")}
                </Tab>
              ))}
            </Tab.List>
            <Tab.Panels>
              <Tab.Panel>
                <DataTable
                  columns={salesColumns}
                  data={getFilteredData("sales")}
                  customStyles={customStyles}
                  pagination
                  striped
                  highlightOnHover
                />
                <div className="mt-2 text-right font-semibold text-lg">
                  Total: ₹{" "}
                  {formatNumber(
                    getTotalAmount("sales", getFilteredData("sales"))
                  )}
                </div>
              </Tab.Panel>
              <Tab.Panel>
                <DataTable
                  columns={purchaseColumns}
                  data={getFilteredData("purchase")}
                  customStyles={customStyles}
                  pagination
                  striped
                  highlightOnHover
                />
                <div className="mt-2 text-right font-semibold text-lg">
                  Total: ₹{" "}
                  {formatNumber(
                    getTotalAmount("purchase", getFilteredData("purchase"))
                  )}
                </div>
              </Tab.Panel>
              <Tab.Panel>
                <DataTable
                  columns={incomeColumns}
                  data={getFilteredData("income")}
                  customStyles={customStyles}
                  pagination
                  striped
                  highlightOnHover
                />
                <div className="mt-2 text-right font-semibold text-lg">
                  Total: ₹{" "}
                  {formatNumber(
                    getTotalAmount("income", getFilteredData("income"))
                  )}
                </div>
              </Tab.Panel>
              <Tab.Panel>
                <DataTable
                  columns={expenseColumns}
                  data={getFilteredData("expenses")}
                  customStyles={customStyles}
                  pagination
                  striped
                  highlightOnHover
                />
                <div className="mt-2 text-right font-semibold text-lg">
                  Total: ₹{" "}
                  {formatNumber(
                    getTotalAmount("expenses", getFilteredData("expenses"))
                  )}
                </div>
              </Tab.Panel>
              <Tab.Panel>
                <DataTable
                  columns={serviceColumns}
                  data={getFilteredData("service_log")}
                  customStyles={customStyles}
                  pagination
                  striped
                  highlightOnHover
                />
              </Tab.Panel>
              <Tab.Panel>
                <DataTable
                  columns={pendingCustomerColumns}
                  data={getFilteredData("pending_customers")}
                  customStyles={customStyles}
                  pagination
                  striped
                  highlightOnHover
                />
              </Tab.Panel>
              <Tab.Panel>
                <div className="mb-8">
                  <div className="flex flex-col md:flex-row flex-wrap gap-8 justify-center items-center mb-8">

                    <div className="bg-white dark:bg-gray-900 shadow-lg rounded-xl p-8 flex-1 min-w-[230px] text-center border border-purple-200">
                      <h3 className="text-2xl font-semibold text-purple-800 mb-3">Total Income</h3>
                      <div className="text-3xl font-bold text-green-600">
                        ₹ {formatNumber(profitLossData.length ? profitLossData[profitLossData.length - 1].income : 0)}
                      </div>
                    </div>

                    <div className="bg-white dark:bg-gray-900 shadow-lg rounded-xl p-8 flex-1 min-w-[230px] text-center border border-purple-200">
                      <h3 className="text-2xl font-semibold text-purple-800 mb-3">Total Expense</h3>
                      <div className="text-3xl font-bold text-red-600">
                        ₹ {formatNumber(profitLossData.length ? profitLossData[profitLossData.length - 1].expense : 0)}
                      </div>
                    </div>

                    <div className="bg-white dark:bg-gray-900 shadow-lg rounded-xl p-8 flex-1 min-w-[230px] text-center border border-green-300">
                      <h3 className="text-2xl font-semibold text-green-700 mb-3">Profit</h3>
                      <div className="text-3xl font-bold text-green-600">
                        ₹ {formatNumber(
                          profitLossData.length && profitLossData[profitLossData.length - 1].profit > 0
                            ? profitLossData[profitLossData.length - 1].profit
                            : 0
                        )}
                      </div>
                    </div>

                    <div className="bg-white dark:bg-gray-900 shadow-lg rounded-xl p-8 flex-1 min-w-[230px] text-center border border-red-300">
                      <h3 className="text-2xl font-semibold text-red-700 mb-3">Loss</h3>
                      <div className="text-3xl font-bold text-red-600">
                        ₹ {formatNumber(
                          profitLossData.length && profitLossData[profitLossData.length - 1].profit < 0
                            ? Math.abs(profitLossData[profitLossData.length - 1].profit)
                            : 0
                        )}
                      </div>
                    </div>

                  </div>


                  <div className="mt-6 w-full max-w-3xl mx-auto">
                    <h3 className="text-lg font-semibold mb-3 text-purple-800 text-center">
                      Monthly Income & Expense Trend
                    </h3>
                    <Line data={lineData} options={lineOptions} height={110} />
                  </div>

                  {/* === Table Section === */}
                  <div className="mt-8">
                    <DataTable
                      columns={profitLossColumns}
                      data={profitLossData}
                      customStyles={customStyles}
                      striped
                      highlightOnHover
                      pagination
                    />
                  </div>
                </div>
              </Tab.Panel>

            </Tab.Panels>
          </Tab.Group>
        </div>
      </div>
    </div>
  );
};

export default Report;
